import type { DefaultOptionType } from '@/components';
import { t } from '@/i18n';
import type { z } from 'zod';
import type { PinpointFieldEnum } from '../models/pinpoint';
import { generatePinpointKey } from './generate-pinpoint-key';
import {
  KsInvestmentBasicInformationScenePinpointFieldEnum,
  KsInvestmentFundBusinessScenePinpointFieldEnum,
  KsInvestmentFundInvestmentsScenePinpointFieldEnum,
  KsInvestmentRelatedMaterialScenePinpointFieldEnum,
} from './ks-investment-pinpoint';
import {
  KsLocalGovernmentActivitiesScenePinpointFieldEnum,
  KsLocalGovernmentActivityFlowScenePinpointFieldEnum,
  KsLocalGovernmentBasicInformationScenePinpointFieldEnum,
  KsLocalGovernmentExecutionProgressScenePinpointFieldEnum,
  KsLocalGovernmentFundAdditionalAttributesScenePinpointFieldEnum,
  KsLocalGovernmentRelatedMaterialScenePinpointFieldEnum,
} from './ks-local-government-pinpoint';
import {
  KsStandardActivitiesScenePinpointFieldEnum,
  KsStandardActivityFlowScenePinpointFieldEnum,
  KsStandardBasicInformationScenePinpointFieldEnum,
  KsStandardFundAdditionalAttributesScenePinpointFieldEnum,
  KsStandardFundBusinessScenePinpointFieldEnum,
  KsStandardIncomeAndExpenditureScenePinpointFieldEnum,
  KsStandardInspectionEvaluationScenePinpointFieldEnum,
  KsStandardOwnershipRatioScenePinpointFieldEnum,
  KsStandardPaymentScenePinpointFieldEnum,
  KsStandardRelatedMaterialScenePinpointFieldEnum,
} from './ks-standard-pinpoint';
import { pinpointLabelMap } from './pinpoint-label-map';
import {
  RsActivitiesScenePinpointFieldEnum,
  RsActivityFlowScenePinpointFieldEnum,
  RsBasicInformationScenePinpointFieldEnum,
  RsDetailedBreakdownScenePinpointFieldEnum,
  RsInspectionEvaluationScenePinpointFieldEnum,
  RsPaymentScenePinpointFieldEnum,
  RsRelatedMaterialScenePinpointFieldEnum,
} from './rs-pinpoint';
import {
  RsSkippedReviewBasicInformationScenePinpointFieldEnum,
  RsSkippedReviewDetailedBreakdownScenePinpointFieldEnum,
  RsSkippedReviewRelatedMaterialScenePinpointFieldEnum,
} from './rs-skipped-review-pinpoint';
import {
  SsActivitiesScenePinpointFieldEnum,
  SsActivityFlowScenePinpointFieldEnum,
  SsBasicInformationScenePinpointFieldEnum,
  SsDetailedBreakdownScenePinpointFieldEnum,
  SsInspectionEvaluationScenePinpointFieldEnum,
  SsPaymentScenePinpointFieldEnum,
  SsProjectRelatedMaterialScenePinpointFieldEnum,
} from './ss-pinpoint';

const SHEET_MAPPING = {
  RS: t('review-sheet'),
  RS_SKIPPED_REVIEW: t('skip-administrative-reform-team-evaluation'),
  SS: t('segment-sheet'),
  KS_STANDARD: t('fund-sheet'),
  KS_LOCAL_GOVERNMENT: t('local-government-fund-sheet'),
  KS_INVESTMENT: t('investment-fund-sheet'),
};
const createOptions = (
  sheetType: keyof typeof SHEET_MAPPING,
  tabName: string,
  fields: z.infer<typeof PinpointFieldEnum>[],
): DefaultOptionType[] => {
  return fields.map((field) => {
    const label = pinpointLabelMap(field);
    return {
      label: `${SHEET_MAPPING[sheetType]}/${tabName}/${label}`,
      value: generatePinpointKey(field),
    };
  });
};

const rsOptions: DefaultOptionType[] = [
  ...createOptions(
    'RS',
    t('basic-information'),
    RsBasicInformationScenePinpointFieldEnum.options,
  ),
  ...createOptions(
    'RS',
    t('budget-and-execution'),
    RsDetailedBreakdownScenePinpointFieldEnum.options,
  ),
  ...createOptions('RS', t('way-to-effect'), [
    ...RsActivitiesScenePinpointFieldEnum.options,
    ...RsActivityFlowScenePinpointFieldEnum.options,
  ]),
  ...createOptions(
    'RS',
    t('inspection-evaluation'),
    RsInspectionEvaluationScenePinpointFieldEnum.options,
  ),
  ...createOptions('RS', t('payment'), RsPaymentScenePinpointFieldEnum.options),
  ...createOptions(
    'RS',
    t('related-material'),
    RsRelatedMaterialScenePinpointFieldEnum.options,
  ),
];

const rsSkippedReviewOptions: DefaultOptionType[] = [
  ...createOptions(
    'RS_SKIPPED_REVIEW',
    t('basic-information'),
    RsSkippedReviewBasicInformationScenePinpointFieldEnum.options,
  ),
  ...createOptions(
    'RS_SKIPPED_REVIEW',
    t('budget-and-execution'),
    RsSkippedReviewDetailedBreakdownScenePinpointFieldEnum.options,
  ),
  ...createOptions(
    'RS_SKIPPED_REVIEW',
    t('related-material'),
    RsSkippedReviewRelatedMaterialScenePinpointFieldEnum.options,
  ),
];

const ssOptions: DefaultOptionType[] = [
  ...createOptions(
    'SS',
    t('basic-information'),
    SsBasicInformationScenePinpointFieldEnum.options,
  ),
  ...createOptions(
    'SS',
    t('budget-and-execution'),
    SsDetailedBreakdownScenePinpointFieldEnum.options,
  ),
  ...createOptions('SS', t('way-to-effect'), [
    ...SsActivitiesScenePinpointFieldEnum.options,
    ...SsActivityFlowScenePinpointFieldEnum.options,
  ]),
  ...createOptions(
    'SS',
    t('inspection-evaluation'),
    SsInspectionEvaluationScenePinpointFieldEnum.options,
  ),
  ...createOptions('SS', t('payment'), SsPaymentScenePinpointFieldEnum.options),
  ...createOptions(
    'SS',
    t('related-material'),
    SsProjectRelatedMaterialScenePinpointFieldEnum.options,
  ),
];

const ksStandardOptions: DefaultOptionType[] = [
  ...createOptions(
    'KS_STANDARD',
    t('basic-information'),
    KsStandardBasicInformationScenePinpointFieldEnum.options,
  ),
  ...createOptions(
    'KS_STANDARD',
    t('fund-additional-attributes'),
    KsStandardFundAdditionalAttributesScenePinpointFieldEnum.options,
  ),
  ...createOptions('KS_STANDARD', t('way-to-effect'), [
    ...KsStandardActivitiesScenePinpointFieldEnum.options,
    ...KsStandardActivityFlowScenePinpointFieldEnum.options,
  ]),
  ...createOptions(
    'KS_STANDARD',
    t('income-and-expenditure-title'),
    KsStandardIncomeAndExpenditureScenePinpointFieldEnum.options,
  ),
  ...createOptions(
    'KS_STANDARD',
    t('fund-business'),
    KsStandardFundBusinessScenePinpointFieldEnum.options,
  ),
  ...createOptions(
    'KS_STANDARD',
    t('ownership-ratio'),
    KsStandardOwnershipRatioScenePinpointFieldEnum.options,
  ),
  ...createOptions(
    'KS_STANDARD',
    t('inspection-evaluation'),
    KsStandardInspectionEvaluationScenePinpointFieldEnum.options,
  ),
  ...createOptions(
    'KS_STANDARD',
    t('payment'),
    KsStandardPaymentScenePinpointFieldEnum.options,
  ),
  ...createOptions(
    'KS_STANDARD',
    t('related-material'),
    KsStandardRelatedMaterialScenePinpointFieldEnum.options,
  ),
];

const ksLocalGovernmentOptions: DefaultOptionType[] = [
  ...createOptions(
    'KS_LOCAL_GOVERNMENT',
    t('basic-information'),
    KsLocalGovernmentBasicInformationScenePinpointFieldEnum.options,
  ),
  ...createOptions(
    'KS_LOCAL_GOVERNMENT',
    t('fund-additional-attributes'),
    KsLocalGovernmentFundAdditionalAttributesScenePinpointFieldEnum.options,
  ),
  ...createOptions('KS_LOCAL_GOVERNMENT', t('way-to-effect'), [
    ...KsLocalGovernmentActivitiesScenePinpointFieldEnum.options,
    ...KsLocalGovernmentActivityFlowScenePinpointFieldEnum.options,
  ]),
  ...createOptions(
    'KS_LOCAL_GOVERNMENT',
    t('execution-progress'),
    KsLocalGovernmentExecutionProgressScenePinpointFieldEnum.options,
  ),
  ...createOptions(
    'KS_LOCAL_GOVERNMENT',
    t('related-material'),
    KsLocalGovernmentRelatedMaterialScenePinpointFieldEnum.options,
  ),
];

const ksInvestmentOptions: DefaultOptionType[] = [
  ...createOptions(
    'KS_INVESTMENT',
    t('basic-information'),
    KsInvestmentBasicInformationScenePinpointFieldEnum.options,
  ),
  ...createOptions(
    'KS_INVESTMENT',
    t('investment'),
    KsInvestmentFundInvestmentsScenePinpointFieldEnum.options,
  ),
  ...createOptions(
    'KS_INVESTMENT',
    t('fund-business'),
    KsInvestmentFundBusinessScenePinpointFieldEnum.options,
  ),
  ...createOptions(
    'KS_INVESTMENT',
    t('related-material'),
    KsInvestmentRelatedMaterialScenePinpointFieldEnum.options,
  ),
];

export const pinpointOptionsBySheet: DefaultOptionType[] = [
  ...rsOptions,
  ...rsSkippedReviewOptions,
  ...ssOptions,
  ...ksStandardOptions,
  ...ksLocalGovernmentOptions,
  ...ksInvestmentOptions,
];
